import * as XLSX from "xlsx";
import { Firestore, collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { Campaign, Customer } from "../../types";
import { CAMPAIGN_CHANNELS, campaignStatusLabel, formatCampaignTimestamp, getCampaignChannel } from "./campaign_details_modal";
import { getCampaignRecipientIds } from "./campaign_recipients";
export { getCampaignRecipientIds } from "./campaign_recipients";

const fitColumns = (rows: Record<string, string | number>[]) => {
    if (!rows.length) {
        return [];
    }
    return Object.keys(rows[0]).map((header) => ({
        wch: Math.min(
            60,
            Math.max(header.length, ...rows.map((row) => String(row[header] ?? "").length)) + 2
        ),
    }));
};

// Custom email bodies are stored as HTML; flatten to plain text for Excel.
const stripHtml = (value?: string) =>
    value?.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() ?? "";

const buildCampaignSummaryRow = (campaign: Campaign) => {
    const whatsappTemplate = campaign.whatsapp?.template ?? campaign.selectedWhatsappTemplate;
    const whatsappBody = whatsappTemplate?.types
        ? Object.values(whatsappTemplate.types)[0]?.body ?? ""
        : "";

    const row: Record<string, string | number> = {
        "Campaign ID": campaign.id,
        "Title": campaign.title ?? "",
        "Channels": CAMPAIGN_CHANNELS
            .filter(({ key }) => getCampaignChannel(campaign, key))
            .map(({ label }) => label)
            .join(", "),
        "Status": campaignStatusLabel(campaign.status),
        "Created By": campaign.created_by ?? "",
        "Created On": formatCampaignTimestamp(campaign.created_on),
        "Run By": campaign.run_by ?? "",
        "Run On": formatCampaignTimestamp(campaign.run_on),
        "Recipients": getCampaignRecipientIds(campaign).length,
        "SMS Message": campaign.sms?.message ?? campaign.smsMessage ?? "",
        "Email Template": campaign.email?.template ?? campaign.selectedEmailTemplate ?? "",
        "Custom Email Subject": campaign.custom_email?.subject ?? campaign.emailSubject ?? "",
        "Custom Email Message": stripHtml(campaign.custom_email?.message ?? campaign.emailMessage),
        "Whatsapp Template": whatsappTemplate?.friendly_name ?? "",
        "Whatsapp Message": whatsappBody,
        "Notification Title": campaign.notification?.title ?? campaign.notificationTitle ?? "",
        "Notification Body": campaign.notification?.body ?? campaign.notificationBody ?? "",
    };

    let totalSum = 0;
    let sentSum = 0;
    let failedSum = 0;

    for (const { key, label } of CAMPAIGN_CHANNELS) {
        const channel = getCampaignChannel(campaign, key);
        row[`${label} Total`] = channel?.message_total ?? 0;
        row[`${label} Sent`] = channel?.message_sent ?? 0;
        row[`${label} Failed`] = channel?.message_failed ?? 0;
        totalSum += channel?.message_total ?? 0;
        sentSum += channel?.message_sent ?? 0;
        failedSum += channel?.message_failed ?? 0;
    }

    row["Total Messages"] = totalSum;
    row["Total Sent"] = sentSum;
    row["Total Failed"] = failedSum;

    return row;
};

const sanitizeFileName = (value: string) =>
    value.replace(/[^a-z0-9؀-ۿ]+/gi, "_").replace(/^_+|_+$/g, "").slice(0, 60) || "campaign";

const dateStamp = () => {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
};

export async function exportCampaignsExcel(db: Firestore): Promise<number> {
    const snapshot = await getDocs(query(collection(db, "campaigns"), orderBy("created_on", "desc")));
    const campaigns = snapshot.docs.map((docSnap) => ({ ...(docSnap.data() as Campaign), id: docSnap.id }));

    const summaryRows = campaigns.map(buildCampaignSummaryRow);

    const errorRows = campaigns.flatMap((campaign) =>
        CAMPAIGN_CHANNELS.flatMap(({ key, label }) =>
            (getCampaignChannel(campaign, key)?.errors ?? []).map((channelError) => ({
                "Campaign ID": campaign.id,
                "Title": campaign.title ?? "",
                "Channel": label,
                "Customer ID": channelError.customer_id ?? "",
                "Error": channelError.message ?? "",
            }))
        )
    );

    const workbook = XLSX.utils.book_new();

    const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
    summarySheet["!cols"] = fitColumns(summaryRows);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Campaigns");

    const errorSheet = XLSX.utils.json_to_sheet(
        errorRows.length ? errorRows : [{ "Campaign ID": "", "Title": "", "Channel": "", "Customer ID": "", "Error": "" }]
    );
    errorSheet["!cols"] = fitColumns(errorRows.length ? errorRows : []);
    XLSX.utils.book_append_sheet(workbook, errorSheet, "Errors");

    XLSX.writeFile(workbook, `campaigns_report_${dateStamp()}.xlsx`);

    return campaigns.length;
}

// Firestore rules only allow `get` on customers (no list queries), so recipient
// details must be fetched one doc at a time, in concurrent batches.
const fetchCustomers = async (db: Firestore, customerIds: string[]): Promise<Map<string, Customer>> => {
    const customers = new Map<string, Customer>();
    const BATCH_SIZE = 100;

    for (let i = 0; i < customerIds.length; i += BATCH_SIZE) {
        const batch = customerIds.slice(i, i + BATCH_SIZE);
        const snapshots = await Promise.all(
            batch.map((customerId) => getDoc(doc(db, "customers", customerId)).catch(() => null))
        );
        snapshots.forEach((snapshot, index) => {
            if (snapshot?.exists()) {
                customers.set(batch[index], snapshot.data() as Customer);
            }
        });
    }

    return customers;
};

export async function exportCampaignRecipientsExcel(db: Firestore, campaign: Campaign): Promise<number> {
    const customerIds = getCampaignRecipientIds(campaign);

    const customers = await fetchCustomers(db, customerIds);

    const activeChannels = CAMPAIGN_CHANNELS.filter(({ key }) => getCampaignChannel(campaign, key));
    const errorsByChannel = new Map(
        activeChannels.map(({ key }) => [
            key,
            new Map(
                (getCampaignChannel(campaign, key)?.errors ?? []).map((channelError) => [
                    channelError.customer_id,
                    channelError.message ?? "",
                ])
            ),
        ])
    );

    const recipientRows = customerIds.map((customerId) => {
        const customer = customers.get(customerId);
        const row: Record<string, string | number> = {
            "Customer ID": customerId,
            "Name (EN)": customer?.name_en ?? "",
            "Name (AR)": customer?.name_ar ?? "",
            "Civil ID": customer?.civilID ?? customer?.other_id ?? "",
            "Mobile": customer?.mobile ? `${customer.mobile_code ?? "+965"} ${customer.mobile}` : "",
            "Email": customer?.email ?? "",
            "Segment": customer?.segment ?? "",
        };

        for (const { key, label } of activeChannels) {
            const errorMessage = errorsByChannel.get(key)?.get(customerId);
            row[`${label} Status`] = campaign.status !== "done"
                ? "Not sent yet"
                : errorMessage !== undefined ? "Failed" : "Sent";
            row[`${label} Error`] = errorMessage ?? "";
        }

        return row;
    });

    const workbook = XLSX.utils.book_new();

    const summaryRow = [buildCampaignSummaryRow(campaign)];
    const summarySheet = XLSX.utils.json_to_sheet(summaryRow);
    summarySheet["!cols"] = fitColumns(summaryRow);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Campaign");

    const recipientsSheet = XLSX.utils.json_to_sheet(
        recipientRows.length ? recipientRows : [{ "Customer ID": "" }]
    );
    recipientsSheet["!cols"] = fitColumns(recipientRows);
    XLSX.utils.book_append_sheet(workbook, recipientsSheet, "Recipients");

    XLSX.writeFile(workbook, `campaign_${sanitizeFileName(campaign.title ?? campaign.id)}_${dateStamp()}.xlsx`);

    return recipientRows.length;
}
